import { FrameSelectorFieldMui } from '@app/components/form/FrameSelectorFieldMui';
import { SwitchCardField } from '@app/components/form/SwitchCardField';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { publishMessages } from '@app/modules/social-export/publish';
import { FormSectionV2 } from '@app/core/components/FormSectionV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid, Stack } from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { DailymotionCategoryField } from './DailymotionCategoryField';
import {
  DailymotionPublishValues,
  useDailymotionPublishSchema,
} from './useDailymotionPublishSchema';

type DailymotionPublishFormProps = BaseExportFormProps<
  socialExport.DailymotionPublishDto,
  socialExport.DailymotionSettingsDto
>;

export function DailymotionPublishForm({
  source,
  onUpload,
  defaultSettings,
  actions,
}: DailymotionPublishFormProps) {
  const { $t } = useIntl();
  const schema = useDailymotionPublishSchema();
  const initialValues: DailymotionPublishValues = {
    title: source.title ?? '',
    content: source.description ?? '',
    tags: source.tags.join(', '),
    forKids: defaultSettings?.forKids ?? false,
    private: defaultSettings?.private ?? true,
    channel: defaultSettings?.channel ?? null,
    thumbOffsetMs: null,
    publishAt: null,
  };

  const beforeSubmit = (data: DailymotionPublishValues): socialExport.DailymotionPublishDto => ({
    title: data.title,
    description: data.content,
    tags: data.tags
      ? data.tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    forKids: data.forKids,
    private: data.private,
    thumbOffsetMs: data.thumbOffsetMs ?? undefined,
    publishAt: data.publishAt || undefined,
    channel: (data.channel as socialExport.DailymotionPublishDtoChannelEnumKey) || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <PublishFormUnsavedChangesWarning />
      <FormSectionV2
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <TitleAndContentFieldsMui />
        <Grid container>
          <Grid item md={8}>
            <TextFieldElement
              name="tags"
              label={$t({
                id: 'socialExport.form.tags',
                defaultMessage: 'Tags',
              })}
              helperText={$t({
                id: 'socialExport.form.commaSeparatedValues',
                defaultMessage: 'Valores separados por coma',
              })}
              fullWidth
            />
          </Grid>
        </Grid>
      </FormSectionV2>
      <FormSectionV2
        title={$t({
          id: 'socialExport.form.settings',
          defaultMessage: 'Parámetros de publicación',
        })}
      >
        <Grid container>
          <Grid item md={8}>
            <DailymotionCategoryField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={12}>
            <Stack spacing={2}>
              <SwitchCardField
                name="forKids"
                label={$t({
                  id: 'socialExport.form.forKids',
                  defaultMessage: 'Contenido apto para niños',
                })}
                description={$t(publishMessages.forKidsDescription)}
              />
              <SwitchCardField
                name="private"
                label={$t({
                  id: 'socialExport.form.uploadAsPrivate',
                  defaultMessage: 'Cargar como video privado',
                })}
                description={$t(publishMessages.uploadAsPrivateDescription)}
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <FrameSelectorFieldMui name="thumbOffsetMs" unit="ms" />
          </Grid>
        </Grid>
      </FormSectionV2>
      <FormSectionV2
        title={$t({
          id: 'socialExport.form.schedule',
          defaultMessage: 'Programación',
        })}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSectionV2>
      {actions}
    </FormContainer>
  );
}
